import React, { useEffect, useState } from 'react';
import { documentsApi, policiesApi } from '../services/api';
import { DocumentDto, PolicyDto, CountryProfileDto } from '@shared/contracts';
import { FileText, UploadCloud, ShieldCheck, Eye, Loader2, CheckCircle2, AlertCircle, Globe, Search, Lock, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export const DashboardPage: React.FC = () => {
  const { subscription, walletBalance, isSuperAdmin } = useAuth();
  const [documents, setDocuments] = useState<DocumentDto[]>([]);
  const [policies, setPolicies] = useState<PolicyDto[]>([]);
  const [countryProfiles, setCountryProfiles] = useState<CountryProfileDto[]>([]);
  const [selectedPolicy, setSelectedPolicy] = useState<string>('');
  const [forceCountry, setForceCountry] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Entitlement evaluation based on subscription and pricing model
  const hasActiveSub = isSuperAdmin || Boolean(subscription?.accessGranted && subscription?.hasSubscription);
  const pricingType = subscription?.subscription?.priceModel?.pricingType;
  const isPayAsGo = pricingType === 'pay_as_go' || pricingType === 'tier_pay_as_go';
  const hasBalance = isSuperAdmin || !isPayAsGo || (walletBalance?.balance !== undefined && walletBalance.balance > 0);
  const canUpload = hasActiveSub && hasBalance;
  const marketplaceUrl = subscription?.marketplaceUrl || 'https://marketplace.sensifai.com';

  const fetchData = async () => {
    try {
      const [docsData, polsData, countryData] = await Promise.all([
        documentsApi.list(),
        policiesApi.list(),
        documentsApi.getCountryProfiles(),
      ]);
      setDocuments(docsData || []);
      setPolicies(polsData || []);
      setCountryProfiles(countryData?.profiles || []);
      const defaultPol = polsData?.find((p) => p.isDefault);
      if (defaultPol) setSelectedPolicy(defaultPol.id);
    } catch (e) {
      console.error('Failed to fetch workspace data', e);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canUpload) {
      setError(
        !hasActiveSub
          ? 'Active subscription required. Please subscribe on Sensifai Marketplace to upload and process documents.'
          : 'Insufficient prepaid wallet balance for Pay-As-You-Go. Please top up your wallet on Sensifai Marketplace.'
      );
      return;
    }
    if (!selectedFile) return;

    setIsUploading(true);
    setError('');
    setSuccess('');

    const formData = new FormData();
    formData.append('file', selectedFile);
    if (selectedPolicy) formData.append('policyId', selectedPolicy);
    if (forceCountry) formData.append('country', forceCountry);

    try {
      const res = await documentsApi.upload(formData);
      const cName = res?.countryIntelligence?.countryName || 'Detected Country';
      setSuccess(`Document "${selectedFile.name}" processed! Country: ${cName}. Detections: ${res?.detections?.length || 0} PII entities.`);
      setSelectedFile(null);
      fetchData();
    } catch (err) {
      const error = err as Error;
      setError(error.message || 'Document processing failed');
    } finally {
      setIsUploading(false);
    }
  };

  const getCountryName = (isoCode?: string, defaultName?: string) => {
    if (!isoCode) return defaultName || 'Auto-Detected';
    const found = countryProfiles.find((c) => c.isoCode === isoCode || c.code === isoCode);
    return found ? found.name : defaultName || isoCode;
  };

  const filteredDocs = documents.filter((doc) => {
    const name = (doc.originalName || doc.fileName || '').toLowerCase();
    const country = (doc.countryName || doc.countryIso || '').toLowerCase();
    const query = searchQuery.toLowerCase();
    return name.includes(query) || country.includes(query);
  });

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
        <div>
          <h1 className="text-xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <FileText className="w-5 h-5 text-sky-600" />
            <span>Document Workspace</span>
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Upload documents for PII detection, automated redaction, and compliance checks.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs">
          <div className="px-3 py-1.5 rounded-lg bg-slate-100 border border-slate-200 text-slate-700 font-medium">
            Total Documents: <strong className="text-slate-900 font-semibold">{documents.length}</strong>
          </div>
          <div className="px-3 py-1.5 rounded-lg bg-sky-50 border border-sky-100 text-sky-700 font-medium">
            Extracted PII: <strong className="text-sky-900 font-semibold">{documents.reduce((acc, d) => acc + (d.entityCount || 0), 0)}</strong>
          </div>
        </div>
      </div>

      {/* Upload Zone Card */}
      <div className="bg-white p-4 sm:p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
        <h2 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
          <UploadCloud className="w-4 h-4 text-sky-600" />
          <span>Upload New Document</span>
        </h2>

        {error && (
          <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs font-medium flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-medium flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
            <span>{success}</span>
          </div>
        )}

        {/* Entitlement restriction banner */}
        {!canUpload && (
          <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-amber-100 rounded-lg text-amber-700 shrink-0 mt-0.5">
                <Lock className="w-5 h-5" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-amber-950">
                  {!hasActiveSub ? 'Active Subscription Required' : 'Prepaid Balance Depleted'}
                </h4>
                <p className="text-xs text-amber-800 mt-0.5">
                  {!hasActiveSub
                    ? 'Document upload and automated redaction require an active subscription on Sensifai Marketplace.'
                    : 'Your Pay-As-You-Go plan requires a positive wallet balance. Please top up your wallet to continue processing documents.'}
                </p>
              </div>
            </div>
            <a
              href={marketplaceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-xs font-semibold shadow-sm transition-colors shrink-0"
            >
              <span>{!hasActiveSub ? 'Subscribe on Marketplace' : 'Top Up Wallet'}</span>
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        )}

        <form onSubmit={handleUpload} className="space-y-4">
          <div
            className={`border-2 border-dashed rounded-2xl p-4 sm:p-6 text-center transition-colors ${
              canUpload
                ? 'border-slate-200 hover:border-sky-400 bg-slate-50/50 hover:bg-slate-50'
                : 'border-slate-200 bg-slate-100/60 cursor-not-allowed opacity-60'
            }`}
          >
            <input
              type="file"
              id="file-upload-main"
              className="hidden"
              onChange={handleFileChange}
              accept=".pdf,.png,.jpg,.jpeg,.txt,.tif,.tiff"
              disabled={!canUpload}
            />
            <label
              htmlFor={canUpload ? 'file-upload-main' : undefined}
              className={`flex flex-col items-center space-y-2 ${canUpload ? 'cursor-pointer' : 'cursor-not-allowed'}`}
            >
              {canUpload ? (
                <UploadCloud className="w-8 h-8 text-slate-400" />
              ) : (
                <Lock className="w-8 h-8 text-slate-400" />
              )}
              <div>
                {canUpload ? (
                  <>
                    <span className="text-xs font-semibold text-sky-600 hover:text-sky-700">Choose a file</span>
                    <span className="text-xs text-slate-500"> or drag & drop here</span>
                  </>
                ) : (
                  <span className="text-xs font-medium text-slate-500">
                    Upload disabled {!hasActiveSub ? '(Subscription required)' : '(Wallet balance required)'}
                  </span>
                )}
              </div>
              <p className="text-[11px] text-slate-400">PDF, PNG, JPG, JPEG, TXT up to 25MB</p>
              {selectedFile && (
                <div className="mt-2 text-xs font-medium text-emerald-700 bg-emerald-50 px-3 py-1 rounded-lg border border-emerald-200">
                  Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                </div>
              )}
            </label>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-1">
            <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3 w-full sm:w-auto">
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <span className="text-xs text-slate-500 font-medium whitespace-nowrap">Country:</span>
                <select
                  value={forceCountry}
                  onChange={(e) => setForceCountry(e.target.value)}
                  disabled={!canUpload}
                  className="w-full sm:w-auto bg-white text-slate-700 text-xs rounded-xl px-3 py-1.5 border border-slate-300 focus:outline-none focus:ring-2 focus:ring-sky-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="">Auto-Detect Origin</option>
                  {countryProfiles.map((cp) => (
                    <option key={cp.isoCode || cp.code} value={cp.isoCode || cp.code}>
                      {cp.name} ({cp.isoCode || cp.code})
                    </option>
                  ))}
                </select>
              </div>

              {policies.length > 0 && (
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <span className="text-xs text-slate-500 font-medium whitespace-nowrap">Policy:</span>
                  <select
                    value={selectedPolicy}
                    onChange={(e) => setSelectedPolicy(e.target.value)}
                    disabled={!canUpload}
                    className="w-full sm:w-auto bg-white text-slate-700 text-xs rounded-xl px-3 py-1.5 border border-slate-300 focus:outline-none focus:ring-2 focus:ring-sky-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {policies.map((pol) => (
                      <option key={pol.id} value={pol.id}>
                        {pol.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={!canUpload || !selectedFile || isUploading}
              className="w-full sm:w-auto btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 px-5 py-2 rounded-xl text-xs font-semibold shadow-sm"
            >
              {isUploading ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span>Processing...</span>
                </>
              ) : !canUpload ? (
                <>
                  <Lock className="w-3.5 h-3.5" />
                  <span>Upload Disabled</span>
                </>
              ) : (
                <>
                  <UploadCloud className="w-3.5 h-3.5" />
                  <span>Process Document</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Documents List */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden space-y-3">
        <div className="p-4 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <h2 className="text-sm font-semibold text-slate-800">Recent Documents</h2>
          <div className="relative w-full sm:w-64">
            <Search className="w-3.5 h-3.5 absolute left-3 top-2.5 text-slate-400" />
            <input
              type="text"
              placeholder="Filter documents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-500"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs text-slate-700 min-w-[600px]">
            <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider text-[10px] font-semibold border-b border-slate-200">
              <tr>
                <th className="py-2.5 px-4 whitespace-nowrap">Document</th>
                <th className="py-2.5 px-4 whitespace-nowrap">Origin Country</th>
                <th className="py-2.5 px-4 whitespace-nowrap">PII Found</th>
                <th className="py-2.5 px-4 whitespace-nowrap">Status</th>
                <th className="py-2.5 px-4 whitespace-nowrap">Date</th>
                <th className="py-2.5 px-4 text-right whitespace-nowrap">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredDocs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-slate-400">
                    No documents found. Select a file above to begin.
                  </td>
                </tr>
              ) : (
                filteredDocs.map((doc) => (
                  <tr key={doc.id} className="hover:bg-slate-50/80 transition-colors">
                    <td className="py-3 px-4 font-medium text-slate-900 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-sky-600 shrink-0" />
                      <span className="truncate max-w-xs">{doc.originalName || doc.fileName}</span>
                    </td>
                    <td className="py-3 px-4 text-slate-600">
                      {getCountryName(doc.countryIso, doc.countryName)}
                    </td>
                    <td className="py-3 px-4">
                      <span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded-md font-mono text-[11px] border border-slate-200">
                        {doc.entityCount || 0} Entities
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 text-[11px] font-medium">
                        {doc.status || 'COMPLETED'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-slate-400 text-[11px]">
                      {new Date(doc.createdAt).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <Link
                        to={`/documents/${doc.id}`}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-sky-600 hover:text-sky-800"
                      >
                        <Eye className="w-3.5 h-3.5" />
                        <span>View & Redact</span>
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

